import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import pick from 'lodash/pick';
import isValid from 'date-fns/is_valid';

import {
    AdornedInput,
    Adornment,
    Button,
    Checkbox,
    FormField,
    FormHelp,
    Input,
    Label,
    LabeledControl,
    Switch,
    Text,
    TextButton,
    Spacer,
    Flex,
    IconCalendarOutline,
    DatePickerInput,
} from '@zillow/constellation';
import { Col, Row } from '@zillow/zrs-comet';

import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import {
    StyledMDActionsContainer,
    StyledMDContainer,
    StyledMDContent,
    StyledMDContentContainer,
} from 'app/shared/containers/ModalContainer/StyledComponents';

const emptyUnit = {
    name: '',
    sqft: '',
    dateAvailable: '',
    price: null,
    active: true,
};

const propTypes = {
    defaultUnit: PropTypes.shape({
        name: PropTypes.string,
        sqft: PropTypes.string,
        dateAvailable: PropTypes.string,
        price: PropTypes.number,
        active: PropTypes.bool,
    }),
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

const defaultProps = {
    defaultUnit: emptyUnit,
};

const EditUnitModal = ({
    defaultUnit,
    onSubmit,
    onCancel,
}) => {
    const [unit, setUnit] = useState(defaultUnit);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [unitErrors, setUnitErrors] = useState({});
    const [genericError, setGenericError] = useState();
    const [dateAvailable, setDateAvailable] = useState('');

    useEffect(() => {
        if (defaultUnit.dateAvailable) {
            setDateAvailable(format(new Date(`${defaultUnit.dateAvailable}T10:00:00`), 'MM/DD/YYYY'));
        }
    }, []);

    useEffect(() => {
        const newDateAvailable = new Date(dateAvailable);

        if (isValid(newDateAvailable)) {
            setUnit({ ...unit, dateAvailable: format(newDateAvailable, 'YYYY-MM-DD') });
        } else {
            setUnit({ ...unit, dateAvailable: null });
        }
    }, [dateAvailable]);

    const handleSubmit = () => {
        setIsSubmitting(true);

        onSubmit(pick(unit, Object.keys(emptyUnit)))
            .catch((result) => {
                setIsSubmitting(false);
                if (result.error && result.error.data && result.error.data.errorByFieldName) {
                    setUnitErrors(result.error.data.errorByFieldName);
                } else {
                    setGenericError('Something went wrong, please try again later.');
                }
            });
    };

    const handleUnitChange = (key, value) => {
        setGenericError();

        setUnit({
            ...unit,
            [key]: value,
        });
    };

    const onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER ||
            event.keyCode === KeyCodeConstants.SPACE) {
            event.target.click();
        }
    };

    return (
        <StyledMDContainer>
            <StyledMDContentContainer>
                <StyledMDContent>
                    <Row paddingY="xs">
                        <Col>
                            <FormField>
                                <Label required={true}>Unit number</Label>
                                <Input
                                    data-test="unit-number"
                                    name="name"
                                    required={true}
                                    error={unitErrors.name}
                                    value={unit.name}
                                    onChange={e => handleUnitChange('name', e.target.value)}
                                />
                                {unitErrors.name && (
                                    <FormHelp error={true} dangerouslySetInnerHTML={{ __html: unitErrors.name }} />
                                )}
                            </FormField>
                        </Col>
                    </Row>
                    <Row paddingY="xs">
                        <Col>
                            <FormField>
                                <Label required={true}>Price</Label>
                                <AdornedInput
                                    data-test="price"
                                    input={(
                                        <Input
                                            error={unitErrors.price}
                                            name="price"
                                            value={unit.price}
                                            onChange={e => handleUnitChange('price', e.target.value)}
                                        />
                                    )}
                                    leftAdornment={<Adornment>$</Adornment>}
                                    rightAdornment={<Adornment>/mo</Adornment>}
                                />
                                {unitErrors.price && (
                                    <FormHelp error={true}>{unitErrors.price}</FormHelp>)}
                            </FormField>
                        </Col>
                    </Row>
                    <Row paddingY="xs">
                        <Col>
                            <FormField>
                                <Label>Date available</Label>
                                <AdornedInput
                                    data-test="date-available"
                                    input={(
                                        <DatePickerInput
                                            value={dateAvailable}
                                            onChange={e => setDateAvailable(e)}
                                        />
                                    )}
                                    rightAdornment={<Adornment><IconCalendarOutline /></Adornment>}
                                />
                                {unitErrors.dateAvailable && (
                                    <FormHelp error={true}>{unitErrors.dateAvailable}</FormHelp>)}
                            </FormField>
                        </Col>
                    </Row>
                    <Row paddingY="xs">
                        <Col>
                            <FormField>
                                <Label>Square feet</Label>
                                <Input
                                    data-test="square-feet"
                                    error={unitErrors.squareFeet}
                                    name="sqft"
                                    value={unit.sqft}
                                    type="number"
                                    onChange={e => handleUnitChange('sqft', e.target.value)}
                                />
                                {unitErrors.squareFeet && (
                                    <FormHelp error={true}>{unitErrors.squareFeet}</FormHelp>)}
                            </FormField>
                        </Col>
                    </Row>
                    <Row paddingY="xs">
                        <Col>
                            <div className="EditUnitModal-switch-container">
                                <LabeledControl
                                    aria-label="Unit available"
                                    tabIndex={0}
                                    role="button"
                                    size="lg"
                                    controlId="Unit-available"
                                    label={(<Label>Unit available</Label>)}
                                    labelPosition="left"
                                    control={(
                                        <Switch
                                            className="EditUnitModal-switch"
                                            checkbox={(
                                                <Checkbox
                                                    checked={unit.active}
                                                    onClick={() => handleUnitChange('active', !unit.active)}
                                                    onKeyUp={onKeyUp}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </div>
                            {unitErrors.active &&
                            <FormHelp>{unitErrors.active}</FormHelp>
                            }
                        </Col>
                    </Row>
                    {genericError && (
                        <Row paddingY="xs">
                            <Col>
                                <Text fontColor="caution" fontType="body">
                                    {genericError}
                                </Text>
                            </Col>
                        </Row>
                    )}
                </StyledMDContent>
            </StyledMDContentContainer>
            <StyledMDActionsContainer>
                <Flex display="flex" justifyContent="flex-end" alignItems="center">
                    <Spacer marginRight="xs">
                        <TextButton
                            aria-label="Cancel"
                            tabIndex={0}
                            role="button"
                            className="EditUnitModal-cancel"
                            onClick={onCancel}
                            onKeyUp={onKeyUp}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </TextButton>
                    </Spacer>
                    <Button
                        data-test="save-button"
                        aria-label="Save"
                        onClick={handleSubmit}
                        disabled={!unit.name || !unit.price || isSubmitting}
                        buttonType="primary"
                    >
                        Save
                    </Button>
                </Flex>
            </StyledMDActionsContainer>
        </StyledMDContainer>
    );
};

EditUnitModal.propTypes = propTypes;
EditUnitModal.defaultProps = defaultProps;

export { EditUnitModal };
export default EditUnitModal;
